import type { RailLayout } from "@/data/rail-branches";
import type { LocationRun } from "../../location/LocationRun";
import { stayOverlapsBranch } from "./stayOverlap";

/** Part run ids are `branch:place`, plus `@startMs` on a later visit to that part. */
export function partRunPlaceId(runId: string): string {
  const place = runId.slice(runId.indexOf(":") + 1);
  const visit = place.indexOf("@");
  return visit === -1 ? place : place.slice(0, visit);
}

export function partBranchAndPlace(
  run: LocationRun,
  rail: RailLayout,
): {
  branchId: string;
  placeId: string;
  rootPlaceId: string;
  siteLabel: string;
  startMs: number;
  endMs: number;
} | null {
  const colon = run.runId.indexOf(":");
  if (colon === -1) {
    return null;
  }
  const branchId = run.runId.slice(0, colon);
  const branch = rail.branches.find((item) => item.id === branchId);
  if (branch === undefined) {
    return null;
  }
  return {
    branchId,
    placeId: partRunPlaceId(run.runId),
    rootPlaceId: branch.rootPlaceId,
    siteLabel: branch.label,
    startMs: branch.startMs,
    endMs: branch.endMs,
  };
}

/** Merged stay intervals inside this part box's own time window. */
export function partStaySegments(
  rail: RailLayout,
  run: LocationRun,
): { startMs: number; endMs: number }[] {
  const key = partBranchAndPlace(run, rail);
  if (key === null) {
    return [];
  }
  const intervals: { startMs: number; endMs: number }[] = [];
  for (const stay of rail.stays) {
    if (!stayOverlapsBranch(stay, key)) {
      continue;
    }
    const placeId = stay.placeId ?? stay.rootPlaceId;
    if (placeId !== key.placeId) {
      continue;
    }
    const label = stay.placeLabel;
    if (label === undefined || label === key.siteLabel) {
      continue;
    }
    intervals.push({
      startMs: Math.max(stay.startMs, key.startMs),
      endMs: Math.min(stay.endMs, key.endMs),
    });
  }
  intervals.sort((a, b) => a.startMs - b.startMs);
  const merged: { startMs: number; endMs: number }[] = [];
  for (const span of intervals) {
    const last = merged[merged.length - 1];
    if (last !== undefined && span.startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, span.endMs);
    } else {
      merged.push({ ...span });
    }
  }
  return merged.filter(
    (span) => span.startMs < run.endMs && span.endMs > run.startMs,
  );
}
